import { useEffect, useState } from 'react';

import { cn } from '~/core/generic/shadcn-utils';
import { formatUtcDateForDisplay } from '~/core/generic/utils/timezoneUtils';
import Button from '~/core/ui/Button';
import { DateRangePicker } from '~/core/ui/DateRangePicker';

import { navigateOut } from '../../utils/navigateOut';

interface DatePickerSDKProps {
  redirectUrl?: string;
  buttonText?: string;
  buttonColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  organizationTimezone?: string;
  defaultPickupTime?: string;
  defaultDropoffTime?: string;
  isMobile?: boolean;
  rounded?: boolean;
}

export function DatePickerSDK({
  redirectUrl = 'example.com/listing/',
  buttonText = 'Check Availability',
  buttonColor = '#007bff',
  primaryColor = '#007bff',
  secondaryColor = '#6c757d',
  organizationTimezone = 'America/Los_Angeles',
  defaultPickupTime,
  defaultDropoffTime,
  isMobile = false,
  rounded = false,
}: DatePickerSDKProps) {
  const [selectedDateRange, setSelectedDateRange] = useState<
    { from: string | null; to: string | null } | undefined
  >(undefined);

  const [isOpen, setIsOpen] = useState(false);
  const [activeField, setActiveField] = useState<'departure' | 'return' | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) {
      setActiveField(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedDateRange?.from && !selectedDateRange?.to) {
      setActiveField('return');
    } else if (selectedDateRange?.from && selectedDateRange?.to) {
      setIsOpen(false);
      setActiveField(null);
    }
  }, [selectedDateRange?.from, selectedDateRange?.to]);

  const handleDateChange = (dates: {
    from: string | null;
    to: string | null;
  }) => {
    if (dates.from === null && dates.to === null) {
      setSelectedDateRange(undefined);
      return;
    }

    // Store the string values in state, not Date objects
    setSelectedDateRange({ from: dates.from, to: dates.to });
  };

  const handleCheckAvailability = () => {
    let url = `${redirectUrl.startsWith('http') ? '' : 'https://'}${redirectUrl}${redirectUrl.endsWith('/') ? '' : '/'}`;

    if (selectedDateRange?.from && selectedDateRange?.to) {
      // Pass UTC ISO strings directly as URL parameters, following BookingWidget pattern
      const checkOutDate = selectedDateRange.from; // UTC ISO string
      const checkInDate = selectedDateRange.to; // UTC ISO string

      url += `?departure=${encodeURIComponent(checkOutDate)}&return=${encodeURIComponent(checkInDate)}`;
    }

    navigateOut(url);
  };

  if (rounded) {
    return (
      <div className="w-full">
        <div className="flex sm:hidden flex-col w-full min-w-[250px]">
          <div className="flex flex-col space-y-3 w-full">
            <div className="flex w-full h-[45px] min-w-[250px]">
              <DateRangePicker
                defaultDates={selectedDateRange}
                onChange={(dates) => {
                  handleDateChange(dates);
                }}
                organizationTimezone={organizationTimezone}
                defaultPickupTime={defaultPickupTime}
                defaultDropoffTime={defaultDropoffTime}
                disablePastDates={true}
                open={isOpen}
                onOpenChange={(
                  value: boolean | ((prevState: boolean) => boolean),
                ) => {
                  setIsOpen(value);
                }}
                numberOfMonths={1}
                className="border-[#CED4DA] rounded-2xl w-full"
                side="bottom"
                customTrigger={
                  <div className="flex w-full h-full">
                    <div
                      className={cn(
                        'grow shrink basis-0 bg-white rounded-l-2xl border border-[#ddddd7] flex-col justify-center items-start inline-flex h-full pl-3 cursor-pointer',
                        activeField === 'departure' && 'border-[#222222]',
                      )}
                      onClick={() => {
                        setActiveField('departure');
                        setIsOpen(true);
                      }}
                    >
                      <div className="text-[#222222] text-[.5rem] font-bold uppercase leading-snug font-sans">
                        START DATE
                      </div>
                      <div className="text-[#222222] text-sm font-semibold uppercase leading-4 font-sans">
                        {selectedDateRange?.from
                          ? formatUtcDateForDisplay(
                              selectedDateRange.from,
                              organizationTimezone,
                              'M/d/yyyy',
                            )
                          : 'Select date'}
                      </div>
                    </div>
                    <div
                      className={cn(
                        'grow shrink basis-0 bg-white rounded-r-2xl border border-[#ddddd7] flex-col justify-center items-start inline-flex h-full pl-3 cursor-pointer',
                        activeField === 'return' && 'border-[#222222]',
                        activeField !== 'return' && 'border-l-0',
                      )}
                      onClick={() => {
                        setActiveField('return');
                        setIsOpen(true);
                      }}
                    >
                      <div className="text-[#222222] text-[.5rem] font-bold uppercase leading-snug font-sans">
                        END DATE
                      </div>
                      <div className="text-[#222222] text-sm font-semibold uppercase leading-4 font-sans">
                        {selectedDateRange?.to
                          ? formatUtcDateForDisplay(
                              selectedDateRange.to,
                              organizationTimezone,
                              'M/d/yyyy',
                            )
                          : 'Select date'}
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
            <div className="w-full">
              <Button
                className="h-[45px] whitespace-nowrap w-full rounded-3xl"
                style={{
                  backgroundColor: buttonColor,
                  borderColor: buttonColor,
                  color: 'white',
                  opacity: 1,
                }}
                onClick={handleCheckAvailability}
              >
                {buttonText}
              </Button>
            </div>
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-[1.5fr_1fr] rounded-[40px] shadow-lg bg-white h-[55px] items-center max-w-[650px] mx-auto">
          <div className="flex justify-center">
            <DateRangePicker
              defaultDates={selectedDateRange}
              onChange={(dates) => {
                handleDateChange(dates);
              }}
              organizationTimezone={organizationTimezone}
              disablePastDates={true}
              open={isOpen}
              onOpenChange={(
                value: boolean | ((prevState: boolean) => boolean),
              ) => {
                setIsOpen(value);
              }}
              numberOfMonths={2}
              className="border-none rounded-[40px] w-full"
              side="bottom"
              customTrigger={
                <div className="flex w-full h-full bg-white rounded-l-[40px] border-none cursor-pointer px-4 items-center">
                  <div className="grid grid-cols-[1fr_25px_40px_25px_1fr] w-full h-full items-center">
                    <div className="flex items-center justify-center w-full ">
                      <div className="text-[#222222] text-sm font-semibold uppercase leading-none font-sans text-right w-full">
                        {selectedDateRange?.from
                          ? formatUtcDateForDisplay(
                              selectedDateRange.from,
                              organizationTimezone,
                              'M/d/yyyy',
                            )
                          : 'START DATE'}
                      </div>
                    </div>
                    <div></div>
                    <div className="flex items-center justify-center text-gray-400">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-gray-400"
                      >
                        <path
                          d="M2 8H14M14 8L10 4M14 8L10 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div></div>
                    <div className="flex items-center justify-center w-full ">
                      <div className="text-[#222222] text-sm font-semibold uppercase leading-none font-sans text-left w-full">
                        {selectedDateRange?.to
                          ? formatUtcDateForDisplay(
                              selectedDateRange.to,
                              organizationTimezone,
                              'M/d/yyyy',
                            )
                          : 'END DATE'}
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
          </div>
          <div className="-ml-2">
            <Button
              className="h-[55px] whitespace-nowrap w-full rounded-[40px] border-none"
              style={{
                backgroundColor: buttonColor,
                borderColor: buttonColor,
                color: 'white',
                opacity: 1,
              }}
              onClick={handleCheckAvailability}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full min-w-[250px] sm:min-w-[400px]">
      <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 w-full">
        <div className="flex w-full h-[45px] min-w-[250px]">
          <DateRangePicker
            defaultDates={selectedDateRange}
            onChange={(dates) => {
              handleDateChange(dates);
            }}
            organizationTimezone={organizationTimezone}
            disablePastDates={true}
            open={isOpen}
            onOpenChange={(
              value: boolean | ((prevState: boolean) => boolean),
            ) => {
              setIsOpen(value);
            }}
            numberOfMonths={isMobile ? 1 : 2}
            className="border-[#CED4DA] rounded-lg w-full"
            side="bottom"
            customTrigger={
              <div className="flex w-full h-full">
                <div
                  className={cn(
                    'grow shrink basis-0 bg-white rounded-l-lg border border-[#ddddd7] flex-col justify-center items-start inline-flex h-full pl-3 cursor-pointer',
                    activeField === 'departure' && 'border-[#222222]',
                  )}
                  onClick={() => {
                    setActiveField('departure');
                    setIsOpen(true);
                  }}
                >
                  <div className="text-[#222222] text-[.5rem] font-bold uppercase leading-snug font-sans">
                    DEPARTURE
                  </div>
                  <div className="text-[#222222] text-sm font-semibold uppercase leading-4 font-sans">
                    {selectedDateRange?.from
                      ? formatUtcDateForDisplay(
                          selectedDateRange.from,
                          organizationTimezone,
                          'M/d/yyyy',
                        )
                      : 'Select date'}
                  </div>
                </div>
                <div
                  className={cn(
                    'grow shrink basis-0 bg-white rounded-r-lg border border-[#ddddd7]  flex-col justify-center items-start inline-flex h-full pl-3 cursor-pointer',
                    activeField === 'return' && 'border-[#222222]',
                    activeField !== 'return' && 'border-l-0',
                  )}
                  onClick={() => {
                    setActiveField('return');
                    setIsOpen(true);
                  }}
                >
                  <div className="text-[#222222] text-[.5rem] font-bold uppercase leading-snug font-sans">
                    RETURN
                  </div>
                  <div className="text-[#222222] text-sm font-semibold uppercase leading-4 font-sans">
                    {selectedDateRange?.to
                      ? formatUtcDateForDisplay(
                          selectedDateRange.to,
                          organizationTimezone,
                          'M/d/yyyy',
                        )
                      : 'Select date'}
                  </div>
                </div>
              </div>
            }
          />
        </div>
        <div className="w-full sm:ml-2">
          <Button
            className="h-[45px] whitespace-nowrap w-full"
            style={{
              backgroundColor: buttonColor,
              borderColor: buttonColor,
              color: 'white',
              opacity: 1,
            }}
            onClick={handleCheckAvailability}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
}
